import {useEffect, useMemo, useState} from 'react';

import {oneOfType, node, func} from 'prop-types';

import {API_ENDPOINTS, SNACKBAR_ACTIONS} from '../const';
import ReportViewsContext from '../contexts/ReportViewsContext';
import useHttp from '../hooks/misc/useHttp';
import useReport from '../hooks/providers/useReport';
import useSnackbar from '../hooks/providers/useSnackbar';
import {areReportViewsFeaturesEnabled} from '../utils';

const fakeSiren = '000000000';

// eslint-disable-next-line complexity
const ReportViewsProvider = ({children}) => {
  const {selectedReport, selectedProject, isReportLoaded} = useReport();
  const {_post, _get} = useHttp();
  const {showSnackbar, closeSnackbar} = useSnackbar();

  const [isSavingView, setIsSavingView] = useState(false);
  const [isSaveReportViewModalOpen, setIsSaveReportViewModalOpen] = useState(false);
  const [allReportsViews, setAllReportsViews] = useState([]);
  const [currentReportViews, setCurrentReportViews] = useState([]);
  const [reportViewsLoading, setReportViewsLoading] = useState(false);
  const [isChooseViewPopperOpen, setIsChooseViewPopperOpen] = useState(false);
  const [chooseViewPopperAnchorEl, setChooseViewPopperAnchorEl] = useState(null);
  const [isDeleteViewModalOpen, setIsDeleteViewModalOpen] = useState(false);
  const [viewToDelete, setViewToDelete] = useState(null);
  const [reportViewsCurrentlyDeleting, setReportViewsCurrentlyDeleting] = useState([]);
  const [reportViewInitialState, setReportViewInitialState] = useState(null);
  const [isReportPristine, setIsReportPristine] = useState(true);

  const [reportViewsFeaturesEnabled, setReportViewsFeaturesEnabled] = useState(false);

  useEffect(() => {
    if (!isReportLoaded) return;

    try {
      const interval = setInterval(async () => {
        const viewsFeaturesEnabled = await areReportViewsFeaturesEnabled(selectedReport);
        setReportViewsFeaturesEnabled(viewsFeaturesEnabled);
      }, 200);

      return () => clearInterval(interval);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log({e});
    }
  }, [isReportLoaded, selectedReport]);

  useEffect(() => {
    const lastAutomaticSave = currentReportViews.find(rv => !rv.is_visible);
    if (lastAutomaticSave) {
      setReportViewInitialState(lastAutomaticSave.report_state);
    }
  }, [currentReportViews]);

  useEffect(() => {
    if (!selectedReport) return;

    const reportViews = allReportsViews?.filter(v => v.report_id.toLowerCase() === selectedReport?.config?.id);
    const filteredViews = selectedProject ? reportViews.filter(v => v.siren?.toString() === selectedProject.siren) : reportViews;
    setCurrentReportViews(filteredViews);
  }, [allReportsViews, selectedReport?.config?.id, selectedProject]);

  const saveReportView = async ({reportId, reportState, viewName = 'auto_save', isAutoSaving = true}) => {
    const url = API_ENDPOINTS.reports.views.save;

    setIsSavingView(true);
    let snackbarId;

    if (isAutoSaving && !reportViewsFeaturesEnabled) return;

    if (!isAutoSaving) {
      snackbarId = showSnackbar(SNACKBAR_ACTIONS.SAVE_REPORT_VIEW_IN_PROGRESS, {
        severity: 'info',
        autoHide: false
      });
    }

    try {
      const newViewData = {
        report_id: reportId,
        report_state_name: viewName,
        report_state: reportState,
        is_visible: !isAutoSaving,
        ...(selectedProject && {siren: selectedProject.siren})
      };

      const {response, responseJson: data} = await _post(url, {
        ...newViewData
      });

      if (!isAutoSaving) {
        closeSnackbar(snackbarId);
      }

      if (response.status === 200) {
        if (!isAutoSaving) {
          showSnackbar(SNACKBAR_ACTIONS.SAVE_REPORT_VIEW_SUCCESS);
        }

        setAllReportsViews(viewsBeforeAdding => {
          const siren = selectedProject?.siren?.toString();

          let updatedViews;

          if (isAutoSaving) {
            // Filter out any existing auto-save with same report_id (optionally + siren)
            updatedViews = viewsBeforeAdding.filter(view => {
              const isSameReport = view.report_id.toLowerCase() === reportId;
              const isAutoSave = !view.is_visible;

              if (!siren) {
                // Remove all auto-saves with same report_id
                return !(isAutoSave && isSameReport);
              }

              // Remove all auto-saves with same report_id + siren
              return !(isAutoSave && isSameReport && view.siren?.toString() === siren);
            });
          } else {
            // Keep previous views in non-auto-save case
            updatedViews = [...viewsBeforeAdding];
          }

          updatedViews.push(newViewData);
          return updatedViews;
        });

        return {
          success: true,
          data
        };
      }

      return {success: false};
    } catch (e) {
      console.error(e);
      return {success: false};
    } finally {
      setIsSavingView(false);
    }
  };

  const getReportsViews = async () => {
    const url = API_ENDPOINTS.reports.views.get;

    setReportViewsLoading(true);
    try {
      const {response, responseJson: data} = await _get(url);

      if (response.status === 200) {
        setAllReportsViews(data);
        return {
          success: true,
          data
        };
      }
      return {
        success: false
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setAllReportsViews([]);
      return {success: false};
    } finally {
      setReportViewsLoading(false);
    }
  };

  const deleteView = async view => {
    const url = API_ENDPOINTS.reports.views.delete;

    setReportViewsCurrentlyDeleting(currentDeletingViews => [...currentDeletingViews, view.report_state_name]);

    const snackbarId = showSnackbar(SNACKBAR_ACTIONS.DELETE_REPORT_VIEW_IN_PROGRESS, {
      severity: 'info',
      autoHide: false
    });

    try {
      const {response} = await _post(url, {
        report_id: selectedReport.config.id,
        siren: selectedProject ? selectedProject.siren : fakeSiren,
        report_state_name: view.report_state_name
      });

      closeSnackbar(snackbarId);

      if (response.status === 200) {
        showSnackbar(SNACKBAR_ACTIONS.DELETE_REPORT_VIEW_SUCCESS);
        setAllReportsViews(viewBeforeDeletion => viewBeforeDeletion.filter(viewBeforeDeletionItem => viewBeforeDeletionItem.report_state_name !== view.report_state_name));
        return {
          success: true
        };
      }
      return {
        success: false
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {success: false};
    } finally {
      setReportViewsCurrentlyDeleting(currentDeletingViews => currentDeletingViews.filter(viewName => viewName !== view.report_state_name));
    }
  };

  const useMemoDeps = [
    saveReportView,
    isSavingView,
    isSaveReportViewModalOpen,
    setIsSaveReportViewModalOpen,
    currentReportViews,
    reportViewsLoading,
    isChooseViewPopperOpen,
    setIsChooseViewPopperOpen,
    chooseViewPopperAnchorEl,
    setChooseViewPopperAnchorEl,
    isDeleteViewModalOpen,
    setIsDeleteViewModalOpen,
    viewToDelete,
    setViewToDelete,
    deleteView,
    reportViewsCurrentlyDeleting,
    reportViewInitialState,
    isReportPristine,
    setIsReportPristine,
    allReportsViews,
    reportViewsFeaturesEnabled,
    setAllReportsViews
  ];

  const value = useMemo(
    () => ({
      getReportsViews,
      isSavingView,
      saveReportView,
      isSaveReportViewModalOpen,
      setIsSaveReportViewModalOpen,
      reportViews: currentReportViews,
      reportViewsLoading,
      isChooseViewPopperOpen,
      setIsChooseViewPopperOpen,
      chooseViewPopperAnchorEl,
      setChooseViewPopperAnchorEl,
      isDeleteViewModalOpen,
      setIsDeleteViewModalOpen,
      viewToDelete,
      setViewToDelete,
      deleteView,
      reportViewsCurrentlyDeleting,
      reportViewInitialState,
      isReportPristine,
      setIsReportPristine,
      allReportsViews,
      reportViewsFeaturesEnabled,
      setAllReportsViews
    }),
    useMemoDeps
  );

  return <ReportViewsContext.Provider value={value}>{children}</ReportViewsContext.Provider>;
};

ReportViewsProvider.propTypes = {
  children: oneOfType([node, func]).isRequired
};

export default ReportViewsProvider;
